import React, { useState } from 'react';
import { Form, Input, Button, Typography, message, Row, Col } from 'antd';
import { PhoneOutlined, LockOutlined, CheckCircleOutlined } from '@ant-design/icons';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { hosturl } from '../libs/Constants';
// const hosturl ="http://192.168.0.7:5300"
const { Title } = Typography;

const ForgotPassword = () => {
    const [form] = Form.useForm();
    const [showOtpField, setShowOtpField] = useState(false);
    const [otpVerified, setOtpVerified] = useState(false);
    const [loading, setLoading] = useState(false);
    const [verificationId, setVerificationId] = useState("");
    const navigate = useNavigate();

    const requestOtp = async () => {
        const phoneNumber = form.getFieldValue('phoneNumber');
        setLoading(true);
        try {
            const response = await axios.post(`${hosturl}/forgot-password`, { phoneNumber });
            if (response.data.verificationId) {
                message.success("OTP sent to your phone.");
                setVerificationId(response.data.verificationId);
                setShowOtpField(true);
            }
        } catch (error) {
            message.error(error.response?.data?.error || "Failed to send OTP. Please try again.");
        } finally {
            setLoading(false);
        }
    };

    const verifyOtp = async () => {
        const otp = form.getFieldValue('otp');
        try {
            const response = await axios.post(`${hosturl}/verify-otp-reset`, { verificationId, otp });
            if (response.data.success) {
                message.success("OTP verified successfully!");
                setOtpVerified(true);
            } else {
                message.error("Invalid OTP.");
            }
        } catch (error) {
            message.error(error.response?.data?.error || "Failed to verify OTP.");
        }
    };

    const resetPassword = async () => {
        const newPassword = form.getFieldValue('newPassword');
        try {
            const response = await axios.post(`${hosturl}/reset-password`, { verificationId, newPassword });
            if (response.data.message) {
                message.success("Password has been reset successfully!");
                navigate('/');
            }
        } catch (error) {
            message.error(error.response?.data?.error || "Failed to reset password.");
        }
    };

    return (
        <div style={styles.container}>
            <div style={styles.card}>
                <Title level={3} style={styles.heading} >Forgot Password</Title>

                <Form
                    form={form}
                    name="forgotPassword"
                    layout="vertical"
                    style={styles.form}
                >
                    <Row gutter={16}>
                        <Col span={24}>
                            <Form.Item
                                name="phoneNumber"
                                label="Phone Number"
                                rules={[{ required: true, message: 'Please enter your phone number!' }]}
                            >
                                <Input
                                    prefix={<PhoneOutlined />}
                                    placeholder="Phone Number"
                                    disabled={otpVerified || showOtpField}
                                    style={styles.input}
                                />
                            </Form.Item>
                        </Col>
                    </Row>

                    {showOtpField && (
                        <Row gutter={16}>
                            <Col span={24}>
                                <Form.Item
                                    name="otp"
                                    label="Enter OTP"
                                    rules={[{ required: true, message: 'Please enter the OTP sent to your phone!' }]}
                                >
                                    <Input
                                        placeholder="OTP"
                                        onPressEnter={verifyOtp}
                                        disabled={otpVerified}
                                        style={styles.input}
                                        suffix={otpVerified && <CheckCircleOutlined style={{ color: 'green' }} />}
                                    />
                                </Form.Item>
                            </Col>
                        </Row>
                    )}

                    {otpVerified && (
                        <>
                            <Row gutter={16}>
                                <Col span={24}>
                                    <Form.Item
                                        name="newPassword"
                                        label="New Password"
                                        rules={[{ required: true, message: 'Please enter your new password!' }]}
                                    >
                                        <Input.Password
                                            prefix={<LockOutlined />}
                                            placeholder="New Password"
                                            style={styles.input}
                                        />
                                    </Form.Item>
                                </Col>
                            </Row>

                            <Row gutter={16}>
                                <Col span={24}>
                                    <Form.Item
                                        name="confirmPassword"
                                        label="Confirm Password"
                                        dependencies={['newPassword']}
                                        rules={[
                                            { required: true, message: 'Please confirm your new password!' },
                                            ({ getFieldValue }) => ({
                                                validator(_, value) {
                                                    if (!value || getFieldValue('newPassword') === value) {
                                                        return Promise.resolve();
                                                    }
                                                    return Promise.reject(new Error('Passwords do not match!'));
                                                },
                                            }),
                                        ]}
                                    >
                                        <Input.Password
                                            prefix={<LockOutlined />}
                                            placeholder="Confirm Password"
                                            style={styles.input}
                                        />
                                    </Form.Item>
                                </Col>
                            </Row>
                        </>
                    )}

                    {!otpVerified ? (
                        <>
                            <Form.Item>
                                <Button
                                    type="primary"
                                    onClick={requestOtp}
                                    loading={loading}
                                    block
                                    style={styles.button}
                                >
                                    {showOtpField ? 'Resend OTP' : 'Send OTP'}
                                </Button>
                            </Form.Item>
                            {showOtpField && (
                                <Form.Item>
                                    <Button
                                        type="primary"
                                        onClick={verifyOtp}
                                        block
                                        style={styles.button}
                                    >
                                        Verify OTP
                                    </Button>
                                </Form.Item>
                            )}
                        </>
                    ) : (
                        <Form.Item>
                            <Button
                                type="primary"
                                onClick={resetPassword}
                                block
                                style={styles.button}
                            >
                                Reset Password
                            </Button>
                        </Form.Item>
                    )}
                </Form>
            </div>
        </div>
    );
};

const styles = {
    container: {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '100vh',
        backgroundColor: '#f39c12',  // Light background color
    },
    card: {
        width: 400,
        borderRadius: 12,
        boxShadow: '0 6px 20px rgba(0, 0, 0, 0.1)',
        padding: '20px',
        backgroundColor: '#fff',
    },

    form: {
        width: '100%',
    },
    input: {
        height: 40,
        borderRadius: 8,
        padding: '0 10px',
        marginBottom: '10px',
        fontSize: '14px',
        border: '1px solid #dcdfe6',
        backgroundColor: '#F7F8FA',
    },
    button: {
        backgroundColor: '#264734',
        color: '#fff',
        fontWeight: 'bold',
        // marginTop: 20,
        borderRadius: '8px',
        height: '40px',
    },
};

export default ForgotPassword;
